import { HttpClient } from '@adapters/HttpClient';

export interface BeepingTeams {
  commercial_id: number | null;
  company_type: 'sl' | 'freelancer';
  customer_origin:
    | 'organic'
    | 'student'
    | 'affiliate'
    | 'meta_ads'
    | 'linkedin_ads'
    | 'google_ads'
    | 'linkedin'
    | 'instagram'
    | 'blog'
    | 'instagram_prospecting'
    | 'linkedin_prospecting'
    | 'cold_email'
    | 'cold_calling'
    | 'referral'
    | 'unknown';
  billing_frequency: 'weekly' | 'monthly';
  order_type: 'cod' | 'card' | 'hybrid';
  store_url: string | null;
  monthly_order_estimate: number | null;
  order_attachments_enabled: boolean;
  invoice_wallet_compensation: boolean;
  wallet_visible: boolean;
  invoice_expiration: number | null;
  cod_advance_days_after_delivery: number;
  holded_id: string;
  holded_client_account: string;
  taxes: string[];
}

export interface BeepingTeamContacts {
  name: string;
  email: string | null;
  phone: string | null;
  role: string | null;
}

export interface BankAccount {
  holder_name: string;
  iban: string;
  bic: string;
  bank_name: string;
  country_code: string;
  currency: string;
  internal_note: string;
  proof_document_file?: File;
}

export interface CreateClientPayload {
  name: string;
  vat: string;
  address: string;
  zip: string;
  city: string;
  region: string;
  country: string;
  currency: string;
  user: {
    name: string;
    email: string;
    password: string;
    phone: string;
  };
  beeping_teams: BeepingTeams;
  beeping_team_contacts: BeepingTeamContacts[];
  bank_account: BankAccount;
  rate_id: number;
  envelope_id?: string;
  requires_contract?: boolean;
  shipping_rule_preset_id?: number;
}

export interface UpdateClientPayload extends Partial<CreateClientPayload> {
  id: number;
}

const buildFormDataKey = (prefix: string, key: string): string => {
  if (!prefix) {
    return key;
  }
  return `${prefix}[${key}]`;
};

const appendFormDataValue = (formData: FormData, key: string, value: unknown): void => {
  if (value === undefined || value === null) {
    return;
  }

  if (value instanceof File) {
    formData.append(key, value);
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      if (item === undefined || item === null) {
        return;
      }

      if (typeof item === 'object' && !(item instanceof File)) {
        appendFormDataObject(formData, `${key}[${index}]`, item as Record<string, unknown>);
        return;
      }

      if (typeof item === 'boolean') {
        formData.append(`${key}[]`, item ? '1' : '0');
        return;
      }

      formData.append(`${key}[]`, String(item));
    });
    return;
  }

  if (typeof value === 'object') {
    appendFormDataObject(formData, key, value as Record<string, unknown>);
    return;
  }

  if (typeof value === 'boolean') {
    formData.append(key, value ? '1' : '0');
    return;
  }

  formData.append(key, String(value));
};

const appendFormDataObject = (formData: FormData, prefix: string, obj: Record<string, unknown>): void => {
  Object.entries(obj).forEach(([key, value]) => {
    appendFormDataValue(formData, buildFormDataKey(prefix, key), value);
  });
};

const buildCreateClientFormData = (payload: CreateClientPayload): FormData => {
  const formData = new FormData();
  appendFormDataObject(formData, '', payload as unknown as Record<string, unknown>);

  // El backend espera siempre la clave commercial_id, incluso cuando no hay comercial asignado.
  if (payload.beeping_teams?.commercial_id === null || payload.beeping_teams?.commercial_id === undefined) {
    formData.append('beeping_teams[commercial_id]', '');
  }

  // Compatibilidad temporal: algunos entornos backend leen el archivo en la clave plana.
  if (payload.bank_account?.proof_document_file) {
    formData.append('proof_document_file', payload.bank_account.proof_document_file);
  }

  return formData;
};

export class ClientsApiAdapter {
  constructor(private httpClient: HttpClient) {}

  async createClient(payload: CreateClientPayload): Promise<any> {
    const formData = buildCreateClientFormData(payload);
    const response = await this.httpClient.post('/teams-child', formData, true, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response;
  }

  async updateClient(payload: UpdateClientPayload): Promise<any> {
    const { id, ...data } = payload;
    const response = await this.httpClient.put(`/teams-child/${id}`, data);
    return response;
  }

  async getClientById(id: number): Promise<any> {
    const response = await this.httpClient.get(`/teams-child/${id}`);
    return response;
  }
}
